/**
 * packages/shared/src/scoring/horizon-band.ts
 *
 * Horizon-band interpretation of the v5 overall score (RUF-552, design D4 —
 * docs/prds/digital-passport-health-confidence.md). Explicitly NOT a
 * probabilistic RUL — we have zero run-to-failure fleet data, so a "hours
 * remaining" number would be fiction. Instead, the existing overall score
 * (packages/shared/src/scoring/recompute-telemetry-score.ts) is bucketed into
 * an operational-fitness band, reused verbatim by both portals (ops asset
 * detail + customer passport) so the labels never drift between them.
 *
 * Boundaries are inclusive at the low end of each band:
 *   85-100 healthy, 70-84 degraded, 50-69 warning, 0-49 critical.
 */

export type HorizonBand = 'healthy' | 'degraded' | 'warning' | 'critical';

export interface HorizonBandInfo {
  band: HorizonBand;
  label: string;
  /** Operational-fitness framing shown on the passport / asset detail (D4 table). */
  meaning: string;
}

const HORIZON_BAND_INFO: Readonly<Record<HorizonBand, HorizonBandInfo>> = Object.freeze({
  healthy: {
    band: 'healthy',
    label: 'Healthy',
    meaning: 'Fit for long-running, multi-week training jobs',
  },
  degraded: {
    band: 'degraded',
    label: 'Degraded',
    meaning: 'Inspection recommended; medium-duration jobs',
  },
  warning: {
    band: 'warning',
    label: 'Warning',
    meaning: 'Short inference tasks only',
  },
  critical: {
    band: 'critical',
    label: 'Critical',
    meaning: 'Drain / repair; not fit for workload placement',
  },
});

/**
 * Bucket an overall v5 score [0,100] into its horizon band. Boundaries per
 * D4: 85-100 healthy / 70-84 degraded / 50-69 warning / 0-49 critical.
 */
export function horizonBandFromScore(overallScore: number): HorizonBand {
  if (overallScore >= 85) return 'healthy';
  if (overallScore >= 70) return 'degraded';
  if (overallScore >= 50) return 'warning';
  return 'critical';
}

/** Label + operational-fitness meaning for a horizon band — shared UI copy. */
export function horizonBandInfo(band: HorizonBand): HorizonBandInfo {
  return HORIZON_BAND_INFO[band];
}
